"use client";

import * as React from "react";

import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@beweegbaas/ui/components/ui/input-group";
import { cn } from "@beweegbaas/ui/lib/utils";
import { IconEye, IconEyeOff, IconLock } from "@tabler/icons-react";

declare module "react" {
  interface InputHTMLAttributes<T> {
    passwordrules?: string;
  }
}

export interface PasswordFieldProps extends Omit<
  React.ComponentProps<"input">,
  "type" | "value" | "onChange" | "defaultValue"
> {
  value?: string;
  defaultValue?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onValueChange?: (value: string) => void;
  className?: string;
  showToggleButton?: boolean;
  passwordRules?: string;
}

function PasswordField({
  value,
  defaultValue,
  onChange,
  onValueChange,
  className,
  showToggleButton = true,
  passwordRules,
  ...props
}: PasswordFieldProps) {
  const [internalValue, setInternalValue] = React.useState(defaultValue || "");
  const [isVisible, setIsVisible] = React.useState(false);
  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : internalValue;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (!isControlled) {
      setInternalValue(newValue);
    }
    onChange?.(e);
    onValueChange?.(newValue);
  };

  const toggleVisibility = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsVisible((prev) => !prev);
  };

  return (
    <InputGroup className={cn("group", className)}>
      <InputGroupAddon align="inline-start">
        <IconLock
          aria-hidden
          className="text-muted-foreground size-4
            group-data-[disabled=true]/input-group:opacity-50"
        />
      </InputGroupAddon>
      <InputGroupInput
        type={isVisible ? "text" : "password"}
        value={currentValue}
        onChange={handleChange}
        passwordrules={passwordRules}
        {...props}
      />
      {showToggleButton && (
        <InputGroupAddon align="inline-end">
          <InputGroupButton
            size="icon-xs"
            variant="ghost"
            type="button"
            onClick={toggleVisibility}
            aria-label={isVisible ? "Hide password" : "Show password"}
            title={isVisible ? "Hide password" : "Show password"}
          >
            <span className="sr-only">{isVisible ? "Hide password" : "Show password"}</span>
            {isVisible ? (
              <IconEyeOff aria-hidden className="size-4" />
            ) : (
              <IconEye aria-hidden className="size-4" />
            )}
          </InputGroupButton>
        </InputGroupAddon>
      )}
    </InputGroup>
  );
}

export { PasswordField };
